import { forwardRef } from 'react';
import { combineClassNames } from '../../Utils/ClassnameUtils';

export interface GWPButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: 'solid' | 'outline' | 'ghost';
	tone?: 'primary' | 'neutral' | 'danger' | 'success' | 'warning';
	size?: 'sm' | 'md' | 'lg';
	icon?: string;
	iconPosition?: 'left' | 'right';
	loading?: boolean;
}

const GWPButton = forwardRef<HTMLButtonElement, GWPButtonProps>((props, ref) => {
	const {
		children,
		className,
		variant = 'solid',
		tone = 'primary',
		size = 'md',
		icon,
		iconPosition = 'left',
		loading = false,
		disabled,
		type = 'button',
		...rest
	} = props;

	const isDisabled = disabled || loading;
	const hasIcon = Boolean(icon);
	const iconClassName = combineClassNames(icon, 'icon');
	const hasLabel = children !== undefined && children !== null;
	const buttonClassName = combineClassNames(
		'gwp-button',
		variant,
		tone,
		size,
		loading ? 'loading' : undefined,
		hasIcon && !hasLabel ? 'icon-only' : undefined,
		className,
	);

	return (
		<button ref={ref} type={type} className={buttonClassName} disabled={isDisabled} {...rest}>
			{hasIcon && iconPosition === 'left' && (
				<span className="button-visual" aria-hidden="true">
					<i className={combineClassNames(iconClassName, loading ? 'is-hidden' : undefined)} />
					<span className={combineClassNames('spinner', !loading ? 'is-hidden' : undefined)} />
				</span>
			)}
			{loading && !hasIcon && <span className="spinner" aria-hidden="true" />}
			{hasLabel && <span className="label">{children}</span>}
			{hasIcon && iconPosition === 'right' && (
				<span className="button-visual" aria-hidden="true">
					<i className={combineClassNames(iconClassName, loading ? 'is-hidden' : undefined)} />
					<span className={combineClassNames('spinner', !loading ? 'is-hidden' : undefined)} />
				</span>
			)}
		</button>
	);
});

GWPButton.displayName = 'GWPButton';

export default GWPButton;
